import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { DailyRecordRow } from "@/lib/database.types";

/**
 * All daily check-in records for the patient, newest first.
 * Powers the Progress page: day card, symptom trends, and the calendar dots.
 */
export function useDailyRecords(patientId: string | undefined) {
  return useQuery<DailyRecordRow[]>({
    queryKey: ["daily_records", patientId],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("daily_records")
        .select("*")
        .eq("patient_id", patientId!)
        .order("record_date", { ascending: false });
      if (error) throw error;
      return (data as DailyRecordRow[] | null) ?? [];
    },
  });
}

export interface SaveDailyRecordInput {
  record_date: string; // YYYY-MM-DD (local)
  weight_lb: number | null;
  calories: number | null;
  exercise_minutes: number | null;
  nausea: number | null;
  heartburn: number | null;
  food_noise: number | null;
  appetite_suppression: number | null;
  notes: string | null;
}

/**
 * Upsert the record for a calendar day (unique on patient_id + record_date).
 * When a weight is provided we ALSO append a weight_logs row so the
 * clinician-facing weight history and the dashboard trend chart pick it up —
 * daily_records is the patient's self-tracking surface, weight_logs stays
 * the canonical weight series.
 */
export function useSaveDailyRecord(patientId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<DailyRecordRow, Error, SaveDailyRecordInput>({
    mutationFn: async (input) => {
      if (!patientId) throw new Error("No patient");
      const { data, error } = await supabase
        .from("daily_records")
        .upsert(
          { patient_id: patientId, ...input, updated_at: new Date().toISOString() },
          { onConflict: "patient_id,record_date" },
        )
        .select("*")
        .single();
      if (error) throw error;

      if (input.weight_lb != null) {
        // Best-effort side write; noon local keeps same-day ordering sane.
        await supabase.from("weight_logs").insert({
          patient_id: patientId,
          weight_lb: input.weight_lb,
          recorded_at: `${input.record_date}T12:00:00`,
          source: "patient_reported",
        });
      }
      return data as DailyRecordRow;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["daily_records", patientId] });
      qc.invalidateQueries({ queryKey: ["weight_logs", patientId] });
    },
  });
}
